import type { ReactNode } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useIsDarkTheme } from '@/stores/ui.store';
import { ChartFrame } from './ChartFrame';
import { ChartTooltip, type ValueFormatter } from './ChartTooltip';
import { chartTheme, seriesColor } from './palette';

export interface BarSeries {
  key: string;
  label: string;
  formatter?: ValueFormatter;
}

export interface BarChartCardProps<T extends Record<string, unknown>> {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  data: T[];
  xKey: string;
  series: BarSeries[];
  height?: number;
  loading?: boolean;
  emptyMessage?: string;
  layout?: 'vertical' | 'horizontal';
  yTickFormatter?: (value: number) => string;
  footer?: ReactNode;
  className?: string;
}

/**
 * Barras comparativas. `layout="vertical"` no Recharts = barras horizontais,
 * usado quando os rótulos das categorias são longos (cidades, planos).
 */
export function BarChartCard<T extends Record<string, unknown>>({
  title,
  description,
  actions,
  data,
  xKey,
  series,
  height = 280,
  loading,
  emptyMessage,
  layout = 'horizontal',
  yTickFormatter,
  footer,
  className,
}: BarChartCardProps<T>) {
  const isDark = useIsDarkTheme();
  const theme = chartTheme(isDark);
  const horizontalBars = layout === 'vertical';
  const formatters = Object.fromEntries(
    series.filter((item) => item.formatter).map((item) => [item.key, item.formatter as ValueFormatter]),
  );

  return (
    <ChartFrame
      title={title}
      description={description}
      actions={actions}
      height={height}
      loading={loading}
      empty={data.length === 0}
      emptyMessage={emptyMessage}
      footer={footer}
      className={className}
    >
      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={data}
          layout={layout}
          margin={{ top: 8, right: 16, bottom: 4, left: horizontalBars ? 8 : 4 }}
          barCategoryGap={horizontalBars ? '25%' : '30%'}
        >
          <CartesianGrid
            stroke={theme.grid}
            strokeDasharray="3 3"
            vertical={horizontalBars}
            horizontal={!horizontalBars}
          />
          {horizontalBars ? (
            <>
              <XAxis
                type="number"
                tickFormatter={yTickFormatter}
                tick={{ fill: theme.text, fontSize: 11 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                type="category"
                dataKey={xKey}
                tick={{ fill: theme.text, fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                width={130}
              />
            </>
          ) : (
            <>
              <XAxis
                dataKey={xKey}
                tick={{ fill: theme.text, fontSize: 11 }}
                axisLine={{ stroke: theme.axis }}
                tickLine={false}
                interval={0}
                height={40}
              />
              <YAxis
                tickFormatter={yTickFormatter}
                tick={{ fill: theme.text, fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                width={64}
              />
            </>
          )}
          <Tooltip
            cursor={{ fill: isDark ? 'rgba(148,163,184,0.08)' : 'rgba(15,23,42,0.04)' }}
            content={<ChartTooltip theme={theme} formatters={formatters} />}
          />
          {series.length > 1 && (
            <Legend iconType="square" wrapperStyle={{ fontSize: 12, color: theme.text, paddingTop: 8 }} />
          )}
          {series.map((item, index) => (
            <Bar
              key={item.key}
              dataKey={item.key}
              name={item.label}
              fill={seriesColor(theme, index)}
              radius={horizontalBars ? [0, 4, 4, 0] : [4, 4, 0, 0]}
              maxBarSize={horizontalBars ? 22 : 42}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </ChartFrame>
  );
}
